import { useTu } from "@src/appV2/i18n/useTu";
import SearchIcon from "@mui/icons-material/Search";
import { Box, ButtonBase, Popover, Stack, Tooltip } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { Avatar } from "@src/appV2/lib/Avatar";
import { useOmnisearch } from "@src/appV2/Omnisearch";
import { getShortcutHint } from "@src/appV2/Omnisearch/components/KeyboardHints";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { useRef } from "react";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { AccountSwitcherMenuWorkplaceDesktop } from "./AccountSwitcherDropdownWorkplace";

export function AccountSwitcherDesktop() {
  const { admin } = useSession();

  if (admin) {
    return <AdminSwitcher />;
  }

  return <WorkplaceSwitcher />;
}

/**
 * Admin: thin "search field" styled button that opens the shared Omnisearch
 * dialog (same one Cmd+K opens). When the admin is currently impersonating a
 * workplace/worker, a "Return to admin" icon button appears beside it.
 */
function AdminSwitcher() {
  const { tu } = useTu("navigation");

  const { open: openOmnisearch } = useOmnisearch();

  return (
    <Tooltip title={tu("Search agents or workplaces")}>
      <ButtonBase
        onClick={openOmnisearch}
        aria-label={tu("Open search")}
        sx={(theme) => ({
          minWidth: "14rem",
          height: 38,
          paddingLeft: 3,
          paddingRight: 2,
          gap: 2,
          borderRadius: theme.borderRadius?.medium,
          backgroundColor: theme.palette.navbar?.itemSelectedBackground,
          color: theme.palette.navbar?.itemContent,
          transition: "background-color 120ms ease, color 120ms ease",
          "&:hover": {
            backgroundColor: theme.palette.navbar?.itemSelectedBackground,
            color: theme.palette.navbar?.itemContentSelected,
          },
        })}
      >
        <SearchIcon fontSize="small" />
        <Text variant="body2" semibold sx={{ flex: 1, textAlign: "left" }}>
          {tu("Search")}
        </Text>
        <KeyboardHintPill />
      </ButtonBase>
    </Tooltip>
  );
}

/**
 * Workplace user: avatar button opens a popover listing the associated
 * workplaces the user can switch into.
 */
function WorkplaceSwitcher() {
  const { tu } = useTu("navigation");
  const anchorRef = useRef<HTMLDivElement>(null);
  const {
    switcherTitle,
    switcherSubtitle,
    dropdownVisible,
    onDropdownButtonClicked,
    onDropdownMouseLeave,
    workplaceDetails,
    onUserClicked,
  } = useAccountSwitcher();

  return (
    <Box ref={anchorRef}>
      <ButtonBase
        onClick={onDropdownButtonClicked}
        aria-label={tu("Switch account (current: {{switcherTitle}})", {
          values: { switcherTitle },
        })}
        aria-haspopup="menu"
        aria-expanded={dropdownVisible}
        sx={(theme) => ({
          width: "fit-content",
          minWidth: "250px",
          maxWidth: "300px",
          minHeight: 50,
          paddingX: 3,
          paddingY: 1,
          gap: 3,
          borderRadius: theme.borderRadius?.medium,
          // `color` set here so the caret Icon inherits it via `currentColor`.
          color: theme.palette.navbar?.itemContent,
          transition: "color 120ms ease",
          "&:hover": { color: theme.palette.navbar?.itemContentSelected },
        })}
      >
        <Avatar
          name={switcherTitle}
          size="small"
          sx={(theme) => ({
            bgcolor: theme.palette.navbar?.avatarBackground,
            color: theme.palette.navbar?.avatarText,
            flexShrink: 0,
          })}
        />
        <Stack direction="column" flex={1} minWidth={0} sx={{ lineHeight: 1.2 }}>
          <Text
            variant="body1"
            semibold
            textAlign="left"
            noWrap
            sx={(theme) => ({ color: theme.palette.navbar?.userName })}
          >
            {switcherTitle}
          </Text>
          {switcherSubtitle && (
            <Text
              variant="caption"
              textAlign="left"
              noWrap
              sx={(theme) => ({ color: theme.palette.navbar?.itemContent })}
            >
              {switcherSubtitle}
            </Text>
          )}
        </Stack>
        <Icon type="caret-bottom" sx={{ flexShrink: 0 }} />
      </ButtonBase>

      <Popover
        open={dropdownVisible}
        anchorEl={anchorRef.current}
        onClose={onDropdownMouseLeave}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              width: anchorRef.current?.offsetWidth,
              background: theme.palette.navbar?.background,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
              paddingX: 3,
              paddingY: 3,
            }),
          },
        }}
      >
        <AccountSwitcherMenuWorkplaceDesktop
          workplaceDetails={workplaceDetails}
          onUserClicked={onUserClicked}
        />
      </Popover>
    </Box>
  );
}

function KeyboardHintPill() {
  return (
    <Text
      variant="caption"
      sx={(theme) => ({
        fontFamily: "monospace",
        paddingX: 2,
        paddingY: 1,
        borderRadius: theme.borderRadius?.xSmall,
        border: `1px solid ${theme.palette.navbar?.sectionDivider}`,
        color: theme.palette.navbar?.itemContent,
      })}
    >
      {getShortcutHint()}
    </Text>
  );
}
